<template>
  <content-subpage
    class="add-from-library-content-subpage add-mo"
    :visible="visible"
    :has-parent-subpage="hasParentSubpage"
    :titles="titles"
    :show-content-title="true"
    condensed
    header-sticky
    @content-subpage-hidden="actionHidden"
  >
    <template #main-content>
      <overlay-loader :active="isProcessing" />
      <search-bar
        kind="white"
        active
        filter-type="selection"
        :keyword="searchPageParams.keyword"
        :placeholder="t('search_resource_placeholder_message')"
        @search="handleKeywordSearch"
      />
    </template>
    <template #main-content-bg>
      <template v-if="!hasKeyword">
        <card-list v-if="isLoading">
          <resource-search-card v-for="n in 1" :key="n" loading />
        </card-list>
        <empty-placeholder v-else-if="isEmptySearchResult">
          <template #icon>
            <generic-empty />
          </template>
          {{ emptyBrowseResultMessage }}
          <template v-if="emptyBrowseResultSubMessage" #sub>
            {{ emptyBrowseResultSubMessage }}
          </template>
        </empty-placeholder>
        <card-list v-else size="large">
          <resource-card
            v-for="item in searchResult.data"
            :key="item.uuid"
            v-bind="populateResourceCardProps(item)"
            v-on="populateResourceCardListeners(item)"
          />
        </card-list>
        <cv-pagination
          v-if="showPagination"
          :page="searchPageParams.pageNo"
          :page-sizes="[searchPageParams.pageSize]"
          :forwards-button-disabled="disableForwardsButton"
          :backwards-button-disabled="disableBackwardsButton"
          @change="handlePageChange"
        />
      </template>
      <div v-else class="search-result-layout-wrapper">
        <div v-if="showFilterAccordion" class="layout-side">
          <filter-accordion
            context="add-media-object-from-library"
            :filters="filterOptions"
            :selected-filter="selectedFilter"
            :loading="isLoading"
            @change="handleFilterChanged"
          />
        </div>
        <card-list v-if="isLoading">
          <resource-search-card v-for="n in 20" :key="n" :loading="isLoading" />
        </card-list>
        <div v-else class="layout-main">
          <div class="header">
            <span>{{ totalRecordLabel }}</span>
            <dropdown-component
              :label="t('search_result_sort_by_label')"
              :value="searchPageParams.sortBy"
              class="sort-by-filter"
              @change="handleSortByChange"
            >
              <cv-dropdown-item
                v-for="option in sortByOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.name }}
              </cv-dropdown-item>
            </dropdown-component>
          </div>
          <empty-placeholder v-if="isEmptySearchResult">
            <template #icon>
              <search-empty />
            </template>
            {{ emptySearchResultMessage }}
            <template #sub>
              {{ emptySearchResultSubMessage }}
            </template>
          </empty-placeholder>
          <div v-else class="content">
            <card-list size="">
              <resource-search-card
                v-for="item in searchResult.data"
                :key="item.uuid"
                v-bind="populateResourceSearchCardProps(item)"
                v-on="populateResourceSearchCardListeners(item)"
              />
            </card-list>
            <cv-pagination
              v-if="showPagination"
              :page="searchPageParams.pageNo"
              :page-sizes="[searchPageParams.pageSize]"
              :forwards-button-disabled="disableForwardsButton"
              :backwards-button-disabled="disableBackwardsButton"
              @change="handlePageChange"
            />
          </div>
        </div>
      </div>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from "vue";
import ResourceSearchCard from "@/components/ResourceSearchCard.vue";
import EmptyPlaceholder from "@plugins/common/components/EmptyPlaceholder.vue";
import SearchBar from "@plugins/common/components/SearchBar.vue";
import CardList from "@plugins/card/components/CardList.vue";
import ContentSubpage from "@plugins/content-subpage/components/ContentSubpage.vue";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { useI18n } from "vue-i18n";
import { ResourceCardProps } from "@/common/card-types.ts";
import { useMediaCard } from "@modules/resource-browse/composables/use-media-card.ts";
import { useAddFromLibrarySubpage } from "@modules/resource-search/composables/use-add-from-library-subpage.ts";
import {
  AddMediaObjectFromLibraryContentSubpageProps,
  AddMediaObjectFromLibraryContentSubpageSelectionEvent,
  MediaResultItem,
  ResourceSearchParams,
} from "@modules/resource-search/resource-search-types.ts";
import {
  BROWSE_RESULT_PAGE_SIZE,
  RESOURCE_SEARCH_TYPE,
} from "@modules/resource-search/resource-search-helper.ts";
import DropdownComponent from "@plugins/common/components/DropdownComponent.vue";
import FilterAccordion from "@/components/FilterAccordion.vue";
import OverlayLoader from "@plugins/common/components/OverlayLoader.vue";
import ResourceCard from "@/components/ResourceCard.vue";
import { useMediaSearchCard } from "@modules/resource-search/composables/use-media-search-card.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import GenericEmpty from "@images/placeholder/empty-states/GenericEmpty.svg";
import SearchEmpty from "@images/placeholder/empty-states/SearchEmpty.svg";

// Props
interface Props {
  params: AddMediaObjectFromLibraryContentSubpageProps;
}

const { params: props } = defineProps<Props>();

// Emits
const emit = defineEmits<{
  selected: [data: AddMediaObjectFromLibraryContentSubpageSelectionEvent];
}>();

// State
const searchPageParams = ref<ResourceSearchParams>({
  keyword: "",
  location: props.restrictLocation,
  pageNo: 1,
  pageSize: BROWSE_RESULT_PAGE_SIZE,
  resource: RESOURCE_SEARCH_TYPE.MEDIA,
  resourceType: props.restrictMediaType,
});

const isProcessing = ref<boolean>(false);

// Computed

// Composable
const { passiveApiFailHandler } = useErrorHandler();

// Methods
const onSuccessCallback = () => {
  isProcessing.value = false;
  actionHidden();
};

const onFailureCallback = () => {
  isProcessing.value = false;
  passiveApiFailHandler();
};

const populateResourceCardProps = (item: MediaResultItem): ResourceCardProps => {
  return populateMediaAddFromLibraryBrowseCardProps(item);
};

const populateResourceCardListeners = (item: MediaResultItem): { click: () => void } => {
  return {
    click: () => {
      isProcessing.value = true;
      emit("selected", {
        data: item,
        onSuccess: onSuccessCallback,
        onFailure: onFailureCallback,
      });
    },
  };
};

const populateResourceSearchCardProps = (item: MediaResultItem) => {
  return populateMediaSearchCardProps(item);
};

const populateResourceSearchCardListeners = (item: MediaResultItem) => {
  return {
    click: () => {
      isProcessing.value = true;
      emit("selected", {
        data: item,
        onSuccess: onSuccessCallback,
        onFailure: onFailureCallback,
      });
    },
    download: () => handleDownload(item.uuid),
  };
};

const triggerSearch = async () => {
  isLoading.value = true;
  await fetchSearchData();
  isLoading.value = false;
};

const triggerSearchAndAggregateFilters = async () => {
  isLoading.value = true;
  await fetchSearchData();
  await fetchSearchFilterCountData();
  isLoading.value = false;
};

// Composables
const { t } = useI18n();

const {
  disableForwardsButton,
  disableBackwardsButton,
  emptyBrowseResultMessage,
  emptyBrowseResultSubMessage,
  emptySearchResultMessage,
  emptySearchResultSubMessage,
  filterOptions,
  hasKeyword,
  isLoading,
  isEmptySearchResult,
  searchResult,
  selectedFilter,
  showFilterAccordion,
  showPagination,
  sortByOptions,
  totalRecordLabel,
  fetchSearchData,
  fetchSearchFilterCountData,
  handleFilterChanged,
  handlePageChange,
  handleKeywordSearch,
  handleSortByChange,
} = useAddFromLibrarySubpage(
  searchPageParams,
  props,
  {
    fromMoeLibrary: props.fromMoeLibrary,
    checkCopyRightForMOEResource: props.checkCopyRightForMOEResource,
  },
  triggerSearch,
  triggerSearchAndAggregateFilters,
);

const { populateMediaAddFromLibraryBrowseCardProps } = useMediaCard();

const { populateMediaSearchCardProps, handleDownload } = useMediaSearchCard();

// Content Subpage setup
const { hasParentSubpage, titles, actionHidden, visible, defaultExpose } = useContentSubpage({
  baseTitle: props.baseTitle,
});
defineExpose(defaultExpose);

// Lifecycle hooks
onBeforeMount(async () => {
  await triggerSearch();
});
</script>
